import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { isStaffRole, useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { PhoneInput } from '../../components/ui/phone-input';
import { 
  Package, Plus, Search, QrCode, Phone, User, MapPin,
  Printer, RefreshCw, Loader2, DollarSign, Camera, Image
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { getSystemDetailsUrl } from '../../lib/qrCode';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LuggagePage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [luggage, setLuggage] = useState([]);
  const [trips, setTrips] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedLuggage, setSelectedLuggage] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [parcelPoints, setParcelPoints] = useState([
    'Beitbridge Engen garage', 'Makado', 'West Nicholson', 'Colleen Bawn', 'Gwanda',
    'Esigodini', 'Bulawayo', 'Gweru', 'Kwekwe', 'Kadoma', 'Chegutu', 'Norton',
    'Whitehouse', 'Highglen', 'Roadport', 'Chitungwiza', 'Bubi', 'Lundi', 'Rutenga',
    'Ngundu', 'Chibi turnoff', 'Masvingo', 'Chaka', 'Mvuma', 'Chivhu', 'Beatrice', 'Furtherstone'
  ]);
  const [collectDialogOpen, setCollectDialogOpen] = useState(false);
  const [collectItem, setCollectItem] = useState(null);
  const [collectAmount, setCollectAmount] = useState('');
  const [collectCurrency, setCollectCurrency] = useState('USD');
  const [collectNotes, setCollectNotes] = useState('');
  const [collectSubmitting, setCollectSubmitting] = useState(false);
  const [deliveringRef, setDeliveringRef] = useState(null);
  const [transferItem, setTransferItem] = useState(null);
  const [transferTargetTripId, setTransferTargetTripId] = useState('');
  const [transferReason, setTransferReason] = useState('bus_full');
  const [transferringRef, setTransferringRef] = useState(null);
  const [senderSuggestions, setSenderSuggestions] = useState([]);
  const [senderSearchLoading, setSenderSearchLoading] = useState(false);
  const [showSenderSuggestions, setShowSenderSuggestions] = useState(false);
  const canSearchSenders = isStaffRole(user?.role);
  
  const [newLuggage, setNewLuggage] = useState({
    trip_id: '',
     description: '',
     passenger_ticket_number: '',
    quantity: 1,
    item_type: '',
    declared_value: '',
    declared_value_currency: 'ZAR',
    is_fragile: false,
    sender_name: '',
    sender_phone: '',
    sender_id_number: '',
    receiver_name: '',
    receiver_phone: '',
    origin_station_id: '',
    destination_station_id: '',
    pickup_point: '',
    dropoff_point: '',
    custom_destination: '',  // Free-text custom destination
    special_notes: '',
    is_with_passenger: true,
    is_pay_forward: false,
    pay_forward_balance: '',
    currency: 'USD',
    total_charge: '',
    amount_paid: '',
    is_price_negotiated: false,
    negotiated_price: '',
    negotiation_reason: ''
  });

  useEffect(() => {
    if (!user || !token) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user, token, navigate]);

  useEffect(() => {
    const query = newLuggage.sender_name.trim();
    if (!showCreate || !canSearchSenders || query.length < 3) {
      setSenderSuggestions([]);
      setSenderSearchLoading(false);
      return undefined;
    }

    let active = true;
    const timeout = setTimeout(async () => {
      setSenderSearchLoading(true);
      try {
        const response = await axios.get(`${API_URL}/customers/search`, {
          params: { q: query },
          headers: { Authorization: `Bearer ${token}` }
        });
        if (active) setSenderSuggestions(response.data || []);
      } catch {
        if (active) setSenderSuggestions([]);
      } finally {
        if (active) setSenderSearchLoading(false);
      }
    }, 250);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [newLuggage.sender_name, showCreate, token, canSearchSenders]);

  const applySenderProfile = (customer) => {
    setNewLuggage(prev => ({
      ...prev,
      sender_name: customer.full_name || customer.name || prev.sender_name,
      sender_phone: customer.phone || prev.sender_phone,
      sender_id_number: customer.id_number || customer.passport_number || prev.sender_id_number
    }));
    setShowSenderSuggestions(false);
    setSenderSuggestions([]);
  };

  const fetchData = async () => {
    const isDriverOnly = user?.role === 'driver';
    try {
      const [tripsRes, stationsRes] = await Promise.all([
        isDriverOnly
          ? axios.get(`${API_URL}/driver/my-trips`, { headers: { Authorization: `Bearer ${token}` } })
          : axios.get(`${API_URL}/trips`),
        axios.get(`${API_URL}/stations`)
      ]);
      const now = Date.now();
      setTrips((tripsRes.data || []).filter((trip) =>
        ['scheduled', 'in_transit'].includes(trip.status) &&
        trip.departure_datetime &&
        new Date(trip.departure_datetime).getTime() >= now
      ));
      setStations(stationsRes.data || []);
    } catch (error) {
      console.error('Failed to load trips/stations:', error);
    }

    // Fetch luggage separately (requires auth)
    try {
      const luggageRes = await axios.get(`${API_URL}/luggage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLuggage(luggageRes.data || []);
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error(`Failed to load luggage: ${detail}`);
      }
      console.error('Failed to load luggage:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTripChange = (tripId) => {
    setNewLuggage(prev => ({
      ...prev,
      trip_id: tripId,
      pickup_point: '',
      dropoff_point: ''
    }));
    const selectedTrip = trips.find(t => t.id === tripId);
    if (selectedTrip) {
      const routeName = (selectedTrip.route_name || '').toLowerCase();
      let points = [];
      if (routeName.includes('bulawayo') || routeName.includes('byo')) {
        points = ['Durban', 'Pietermaritzburg', 'Mooi River', 'Beitbridge Engen garage', 'Makado', 'West Nicholson', 'Colleen Bawn', 'Gwanda', 'Esigodini', 'Bulawayo', 'Gweru', 'Kwekwe', 'Kadoma', 'Chegutu', 'Norton', 'Whitehouse', 'Highglen', 'Roadport', 'Chitungwiza'];
      } else if (routeName.includes('masvingo') || routeName.includes('msv')) {
        points = ['Durban', 'Pietermaritzburg', 'Mooi River', 'Beitbridge Engen garage', 'Bubi', 'Lundi', 'Rutenga', 'Ngundu', 'Chibi turnoff', 'Masvingo', 'Chaka', 'Mvuma', 'Chivhu', 'Beatrice', 'Furtherstone', 'Highglen', 'Roadport', 'Chitungwiza'];
      } else {
        points = selectedTrip.parcel_points || selectedTrip.pickup_points || [];
        if (points.length === 0) {
          points = ['Durban', 'Pietermaritzburg', 'Mooi River', 'Beitbridge Engen garage', 'Masvingo', 'Bulawayo', 'Roadport', 'Highglen', 'Chitungwiza'];
        }
      }
      setParcelPoints(points);
    } else {
      setParcelPoints([]);
    }
  };

  const handleTotalChargeChange = (totalVal) => {
    const total = parseFloat(totalVal || 0);
    const paid = parseFloat(newLuggage.amount_paid || 0);
    const isPf = newLuggage.amount_paid !== '' && paid < total;
    setNewLuggage(prev => ({
      ...prev,
      total_charge: totalVal,
      is_pay_forward: isPf,
      pay_forward_balance: isPf ? (total - paid).toFixed(2) : (prev.is_pay_forward ? prev.pay_forward_balance : '0')
    }));
  };

  const handleAmountPaidChange = (paidVal) => {
    const total = parseFloat(newLuggage.total_charge || 0);
    const paid = parseFloat(paidVal || 0);
    const isPf = paidVal !== '' && paid < total;
    setNewLuggage(prev => ({
      ...prev,
      amount_paid: paidVal,
      is_pay_forward: isPf,
      pay_forward_balance: isPf ? (total - paid).toFixed(2) : '0'
    }));
  };

  const handleCreateLuggage = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const totalCharge = parseFloat(newLuggage.total_charge || 0);
      const amountPaid = newLuggage.amount_paid !== '' ? parseFloat(newLuggage.amount_paid || 0) : totalCharge;
      const isPayForward = amountPaid < totalCharge || Boolean(newLuggage.is_pay_forward);
      const payForwardBalance = isPayForward ? Math.max(0, totalCharge - amountPaid) : 0;
      
      const response = await axios.post(`${API_URL}/luggage`, {
        ...newLuggage,
        quantity: parseInt(newLuggage.quantity) || 1,
        declared_value: parseFloat(newLuggage.declared_value || 0),
        total_charge: totalCharge,
        amount_paid: amountPaid,
        is_pay_forward: isPayForward,
        pay_forward_balance: payForwardBalance,
        negotiated_price: newLuggage.is_price_negotiated ? parseFloat(newLuggage.negotiated_price || totalCharge) : null,
        negotiation_reason: newLuggage.is_price_negotiated ? (newLuggage.negotiation_reason || 'Station counter negotiation') : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Luggage ticket created: ${response.data.luggage_ref}`);
      setShowCreate(false);
      setNewLuggage({
        trip_id: '',
        passenger_ticket_number: '',
        description: '',
        quantity: 1,
        item_type: '',
        declared_value: '',
        is_fragile: false,
        sender_name: '',
        sender_phone: '',
        sender_id_number: '',
        receiver_name: '',
        receiver_phone: '',
        origin_station_id: '',
        destination_station_id: '',
        pickup_point: '',
        dropoff_point: '',
        custom_destination: '',
        special_notes: '',
        is_with_passenger: true,
        is_pay_forward: false,
        pay_forward_balance: '',
        currency: 'USD',
        total_charge: '',
        amount_paid: '',
        is_price_negotiated: false,
        negotiated_price: '',
        negotiation_reason: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create luggage ticket');
    } finally {
      setCreating(false);
    }
  };

  const handlePrint = async (luggageRef) => {
    try {
      await axios.post(`${API_URL}/luggage/${luggageRef}/print`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.print();
    } catch (error) {
      toast.error('Failed to record print');
    }
  };

  const handlePhotoUpload = async (luggageRef, event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setUploadingPhoto(true);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        
        await axios.post(`${API_URL}/luggage/${luggageRef}/photo`, 
          { photo_url: base64 },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success('Photo uploaded successfully');
        fetchData(); // Refresh luggage list
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const openCollectDialog = (item) => {
    setCollectItem(item);
    setCollectAmount(String(item.pay_forward_balance || ''));
    setCollectCurrency(item.currency || 'USD');
    setCollectNotes(`Payment received at counter for ${item.luggage_ref}`);
    setCollectDialogOpen(true);
  };

  const handleCollectBalanceSubmit = async () => {
    if (!collectItem) return;
    const amount = parseFloat(collectAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setCollectSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/luggage/${collectItem.luggage_ref}/collect-balance`,
        {
          amount: amount,
          currency: collectCurrency,
          notes: collectNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'Payment recorded successfully!');
      setCollectDialogOpen(false);
      setCollectItem(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setCollectSubmitting(false);
    }
  };

  const handleDeliverLuggage = async (item) => {
    const bal = parseFloat(item.pay_forward_balance || 0);
    if (bal > 0) {
      toast.error(
        `🚫 CANNOT DELIVER: Outstanding balance of ${item.currency === 'ZAR' ? 'R' : '$'}${bal.toFixed(2)} must be paid first!`
      );
      return;
    }

    const collectedByName = window.prompt('Name of person collecting the luggage:', item.receiver_name || '');
    if (!collectedByName?.trim()) return;
    const collectedById = window.prompt('Collector ID or passport number (optional):', '');

    setDeliveringRef(item.luggage_ref);
    try {
      await axios.post(
        `${API_URL}/luggage/${item.luggage_ref}/scan`,
        {
          new_status: 'claimed',
          notes: `Luggage released and delivered to ${collectedByName.trim()}`,
          collected_by_name: collectedByName.trim(),
          collected_by_id_number: collectedById?.trim() || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Luggage ${item.luggage_ref} released to receiver!`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to release luggage');
    } finally {
      setDeliveringRef(null);
    }
  };

  const getNextTransferTrips = (item) => {
    if (!item || !trips.length) return [];
    const currentTrip = trips.find((trip) => trip.id === item.trip_id) || null;
    const currentRouteId = currentTrip?.route_id || item.route_id;
    const currentRouteName = (currentTrip?.route_name || item.route_name || '').trim().toLowerCase();
    if (!currentRouteId && !currentRouteName) return [];

    return trips.filter((trip) => {
      const routeMatch = currentRouteId && trip.route_id
        ? trip.route_id === currentRouteId
        : (trip.route_name || '').trim().toLowerCase() === currentRouteName;
      return trip.id !== item.trip_id && routeMatch && trip.status === 'scheduled';
    }).sort((a, b) => new Date(a.departure_datetime) - new Date(b.departure_datetime));
  };

  const handleTransferLuggage = async () => {
    if (!transferItem) return;
    if (!transferTargetTripId) {
      toast.error('Please select another scheduled trip for the transfer.');
      return;
    }

    setTransferringRef(transferItem.luggage_ref);
    try {
      const res = await axios.post(
        `${API_URL}/luggage/${transferItem.luggage_ref}/transfer`,
        {
          next_trip_id: transferTargetTripId,
          reason: transferReason,
          notes: `Transferred because current bus was full and the luggage needed to move to the next trip.`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'Luggage transferred to another trip.');
      setTransferItem(null);
      setTransferTargetTripId('');
      setTransferReason('bus_full');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to transfer luggage');
    } finally {
      setTransferringRef(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'registered': return 'bg-blue-100 text-blue-800';
      case 'checked_in': return 'bg-yellow-100 text-yellow-800';
      case 'loaded': return 'bg-green-100 text-green-800';
      case 'in_transit': return 'bg-purple-100 text-purple-800';
      case 'offloaded': return 'bg-orange-100 text-orange-800';
      case 'claimed': return 'bg-slate-100 text-slate-800';
      default: return 'bg-red-100 text-red-800';
    }
  };

  const isDriverOnly = user?.role === 'driver';
  // Collect IDs of trips this user is allowed to see (drivers only)
  const assignedTripIds = isDriverOnly ? new Set(trips.map(t => t.id)) : null;

  const filteredLuggage = luggage.filter(l => {
    // Scope to assigned trips only for driver
    if (assignedTripIds && !assignedTripIds.has(l.trip_id)) return false;
    if (!searchTerm) return true;
    return (
      l.luggage_ref.toLowerCase().includes(searchTerm.toLowerCase()) ||
      l.sender_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      l.receiver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      l.sender_phone.includes(searchTerm)
    );
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-panashe-blue" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold">Luggage Management</h1>
            <p className="text-slate-500">Register and track luggage tickets</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={() => navigate('/dashboard/luggage-manifest')}
              className="border-purple-300 text-purple-800 hover:bg-purple-50"
            >
              <Package className="h-4 w-4 mr-2 text-purple-600" />
              Luggage Manifest
            </Button>
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
              <DialogTrigger asChild>
                <Button className="btn-panashe" data-testid="create-luggage-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  New Luggage Ticket
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Register New Luggage</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateLuggage} className="space-y-4">
                {/* Trip Selection */}
                <div>
                  <Label>Trip *</Label>
                  <Select 
                    value={newLuggage.trip_id} 
                    onValueChange={handleTripChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select trip" />
                    </SelectTrigger>
                    <SelectContent>
                      {trips.map(t => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.route_name} - {t.departure_datetime && format(parseISO(t.departure_datetime), 'dd MMM HH:mm')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Passenger ticket number (optional)</Label>
                  <Input
                    value={newLuggage.passenger_ticket_number}
                    onChange={(event) => setNewLuggage({ ...newLuggage, passenger_ticket_number: event.target.value.toUpperCase() })}
                    placeholder="Link this parcel to a passenger ticket"
                  />
                  <p className="text-xs text-slate-500 mt-1">The luggage number will appear on the passenger ticket.</p>
                </div>

                {/* Item Details */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Quantity *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newLuggage.quantity}
                      onChange={(e) => setNewLuggage({...newLuggage, quantity: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>Item Type *</Label>
                    <Select 
                      value={newLuggage.item_type} 
                      onValueChange={(v) => setNewLuggage({...newLuggage, item_type: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="suitcase">Suitcase</SelectItem>
                        <SelectItem value="box">Box/Carton</SelectItem>
                        <SelectItem value="bag">Bag</SelectItem>
                        <SelectItem value="electronics">Electronics</SelectItem>
                        <SelectItem value="documents">Documents</SelectItem>
                        <SelectItem value="food">Food Items</SelectItem>
                        <SelectItem value="furniture">Furniture</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label>Declared Value</Label>
                      <div className="flex items-center gap-1 text-xs">
                        <button
                          type="button"
                          onClick={() => setNewLuggage({...newLuggage, declared_value_currency: 'ZAR'})}
                          className={`px-1.5 py-0.5 rounded font-bold text-[11px] ${newLuggage.declared_value_currency === 'ZAR' ? 'bg-green-600 text-white' : 'bg-slate-200 text-slate-700'}`}
                        >
                          ZAR (R)
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewLuggage({...newLuggage, declared_value_currency: 'USD'})}
                          className={`px-1.5 py-0.5 rounded font-bold text-[11px] ${newLuggage.declared_value_currency === 'USD' ? 'bg-green-600 text-white' : 'bg-slate-200 text-slate-700'}`}
                        >
                          USD ($)
                        </button>
                      </div>
                    </div>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm">
                        {newLuggage.declared_value_currency === 'USD' ? '$' : 'R'}
                      </span>
                      <Input
                        type="number"
                        step="0.01"
                        className="pl-7"
                        value={newLuggage.declared_value}
                        onChange={(e) => setNewLuggage({...newLuggage, declared_value: e.target.value})}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <Label>Description *</Label>
                  <Input
                    value={newLuggage.description}
                    onChange={(e) => setNewLuggage({...newLuggage, description: e.target.value})}
                    placeholder="e.g., Black suitcase, cardboard box with clothes"
                    required
                  />
                </div>

                {/* Fragile checkbox */}
                <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <input
                    type="checkbox"
                    id="isFragile"
                    checked={newLuggage.is_fragile}
                    onChange={(e) => setNewLuggage({...newLuggage, is_fragile: e.target.checked})}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="isFragile" className="text-yellow-800 font-medium">
                    ⚠️ Fragile - Handle with care
                  </Label>
                </div>

                {/* Sender Info */}
                <div className="border-t pt-4">
                  <p className="font-medium mb-2">Sender Information</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Name *</Label>
                      <div className="relative">
                        <Input
                          value={newLuggage.sender_name}
                          onChange={(e) => {
                            setNewLuggage(prev => ({ ...prev, sender_name: e.target.value }));
                            setShowSenderSuggestions(true);
                          }}
                          onFocus={() => setShowSenderSuggestions(true)}
                          placeholder={canSearchSenders ? 'Type first 3 letters to find a returning sender' : 'Enter sender name'}
                          required
                        />
                        {canSearchSenders && showSenderSuggestions && newLuggage.sender_name.trim().length >= 3 && (
                          <div className="absolute z-30 left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border rounded-md shadow-lg">
                            {senderSearchLoading && (
                              <div className="px-3 py-2 text-sm text-slate-500 flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" /> Searching saved senders...
                              </div>
                            )}
                            {!senderSearchLoading && senderSuggestions.length === 0 && (
                              <div className="px-3 py-2 text-sm text-slate-500">No saved sender found</div>
                            )}
                            {!senderSearchLoading && senderSuggestions.map(customer => (
                              <button
                                key={customer.id}
                                type="button"
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-b-0"
                                onClick={() => applySenderProfile(customer)}
                              >
                                <span className="block font-semibold text-slate-800">{customer.full_name || customer.name}</span>
                                <span className="block text-xs text-slate-500">
                                  {customer.phone || 'No phone'}
                                  {customer.id_number || customer.passport_number
                                    ? ` • ID ${customer.id_number || customer.passport_number}`
                                    : ''}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label>Phone *</Label>
                      <PhoneInput
                        value={newLuggage.sender_phone}
                        onChange={(val) => setNewLuggage({...newLuggage, sender_phone: val})}
                        placeholder="77 123 4567"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Receiver Info */}
                <div className="border-t pt-4">
                  <p className="font-medium mb-2">Receiver Information</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={newLuggage.receiver_name}
                        onChange={(e) => setNewLuggage({...newLuggage, receiver_name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <PhoneInput
                        value={newLuggage.receiver_phone}
                        onChange={(val) => setNewLuggage({...newLuggage, receiver_phone: val})}
                        placeholder="77 123 4567"
                      />
                    </div>
                  </div>
                </div>

                {/* Stations */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Origin Station *</Label>
                    <Select 
                      value={newLuggage.origin_station_id} 
                      onValueChange={(v) => setNewLuggage({...newLuggage, origin_station_id: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select origin" />
                      </SelectTrigger>
                      <SelectContent>
                        {stations.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Destination Station *</Label>
                    <Select 
                      value={newLuggage.destination_station_id} 
                      onValueChange={(v) => setNewLuggage({...newLuggage, destination_station_id: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select destination" />
                      </SelectTrigger>
                      <SelectContent>
                        {stations.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Parcel Drop and Pick Points */}
                {parcelPoints.length > 0 && (
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 space-y-3">
                    <p className="text-xs font-semibold text-purple-900 uppercase tracking-wide">📦 Route Parcel Drop &amp; Pick Points</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-purple-800">Parcel Pick-up Point</Label>
                        <Select 
                          value={newLuggage.pickup_point} 
                          onValueChange={(v) => setNewLuggage({...newLuggage, pickup_point: v})}
                        >
                          <SelectTrigger className="bg-white border-purple-300">
                            <SelectValue placeholder="Select pick-up point" />
                          </SelectTrigger>
                          <SelectContent>
                            {parcelPoints.map((pt, i) => (
                              <SelectItem key={i} value={pt}>{pt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-purple-800">Parcel Drop-off Point</Label>
                        <Select 
                          value={newLuggage.dropoff_point} 
                          onValueChange={(v) => setNewLuggage({...newLuggage, dropoff_point: v})}
                        >
                          <SelectTrigger className="bg-white border-purple-300">
                            <SelectValue placeholder="Select drop-off point" />
                          </SelectTrigger>
                          <SelectContent>
                            {parcelPoints.map((pt, i) => (
                              <SelectItem key={i} value={pt}>{pt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Custom Destination - for luggage going to different location */}
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Label className="text-blue-800 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Custom Destination (Optional)
                  </Label>
                  <p className="text-xs text-blue-600 mb-2">
                    If luggage destination differs from the bus station (e.g., a specific address or town)
                  </p>
                  <Input
                    value={newLuggage.custom_destination}
                    onChange={(e) => setNewLuggage({...newLuggage, custom_destination: e.target.value})}
                    placeholder="e.g., Beitbridge Town, 123 Main Street Durban"
                    className="border-blue-300"
                  />
                </div>

                {/* Notes */}
                <div>
                  <Label>Special Notes</Label>
                  <Input
                    value={newLuggage.special_notes}
                    onChange={(e) => setNewLuggage({...newLuggage, special_notes: e.target.value})}
                    placeholder="Any special handling instructions..."
                  />
                </div>

                {/* Options */}
                <div className="space-y-3 border-t pt-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="withPassenger"
                      checked={newLuggage.is_with_passenger}
                      onChange={(e) => setNewLuggage({...newLuggage, is_with_passenger: e.target.checked})}
                      className="h-4 w-4"
                    />
                    <Label htmlFor="withPassenger">Traveling with passenger</Label>
                  </div>
                </div>

                {/* Payment Section */}
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="font-medium text-green-800 mb-3 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Payment Information
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-green-700">Currency *</Label>
                      <Select 
                        value={newLuggage.currency} 
                        onValueChange={(v) => setNewLuggage({...newLuggage, currency: v})}
                      >
                        <SelectTrigger className="border-green-300">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">USD ($)</SelectItem>
                          <SelectItem value="ZAR">ZAR (R)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-green-700">Total Charge ({newLuggage.currency}) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newLuggage.total_charge}
                        onChange={(e) => handleTotalChargeChange(e.target.value)}
                        placeholder="Enter total charge"
                        className="border-green-300"
                        required
                      />
                    </div>
                  </div>
                  
                  {/* STATION PRICE NEGOTIATION */}
                  <div className="mt-3 p-3 bg-indigo-50/80 rounded-lg border border-indigo-200">
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        id="luggageNegotiated"
                        checked={newLuggage.is_price_negotiated}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setNewLuggage(prev => ({
                            ...prev,
                            is_price_negotiated: checked,
                            total_charge: checked && prev.negotiated_price ? prev.negotiated_price : prev.total_charge
                          }));
                          if (checked && newLuggage.negotiated_price) {
                            handleTotalChargeChange(newLuggage.negotiated_price);
                          }
                        }}
                        className="h-4 w-4 text-indigo-600 rounded"
                      />
                      <Label htmlFor="luggageNegotiated" className="font-semibold text-xs text-indigo-900 cursor-pointer">
                        🤝 Negotiate Luggage Rate (Station Counter Processing)
                      </Label>
                    </div>

                    {newLuggage.is_price_negotiated && (
                      <div className="grid grid-cols-2 gap-3 mt-2 pt-2 border-t border-indigo-200">
                        <div>
                          <Label className="text-xs text-indigo-900 font-medium">Agreed Rate ({newLuggage.currency}) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={newLuggage.negotiated_price}
                            onChange={(e) => {
                              const val = e.target.value;
                              setNewLuggage(prev => ({ ...prev, negotiated_price: val }));
                              handleTotalChargeChange(val);
                            }}
                            placeholder="Agreed charge"
                            className="mt-1 bg-white border-indigo-300 font-semibold text-indigo-950"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-indigo-900 font-medium">Negotiation Reason / Notes</Label>
                          <Input
                            value={newLuggage.negotiation_reason}
                            onChange={(e) => setNewLuggage(prev => ({ ...prev, negotiation_reason: e.target.value }))}
                            placeholder="e.g. Regular sender, bulk discount"
                            className="mt-1 bg-white border-indigo-300"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* PAY FORWARD OPTION (AUTO CALCULATED) */}
                  <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        id="payForward"
                        checked={newLuggage.is_pay_forward}
                        onChange={(e) => setNewLuggage({...newLuggage, is_pay_forward: e.target.checked})}
                        className="h-4 w-4"
                      />
                      <Label htmlFor="payForward" className="font-medium text-orange-800">
                        💰 Pay Forward (Auto-enabled when amount paid is less than total charge)
                      </Label>
                    </div>
                    
                    <div className="mt-3 grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-orange-700">Amount Paid Now ({newLuggage.currency})</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newLuggage.amount_paid}
                          onChange={(e) => handleAmountPaidChange(e.target.value)}
                          placeholder="Amount paid now (leave blank for full)"
                          className="mt-1 border-orange-300"
                        />
                      </div>
                      <div>
                        <Label className="text-orange-700">Auto Balance to Collect ({newLuggage.currency})</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newLuggage.pay_forward_balance}
                          onChange={(e) => setNewLuggage({...newLuggage, pay_forward_balance: e.target.value})}
                          placeholder="Balance due at destination"
                          className="mt-1 border-orange-300 font-bold"
                          readOnly={newLuggage.amount_paid !== ''}
                        />
                      </div>
                      {newLuggage.is_pay_forward && (
                        <p className="text-xs text-orange-700 font-semibold col-span-2 bg-orange-100 p-2 rounded">
                          ⚠️ Pay Forward Active: Receiver will settle {newLuggage.currency} {newLuggage.pay_forward_balance} upon collection at destination.
                        </p>
                      )}
                    </div>
                    {!newLuggage.is_pay_forward && newLuggage.total_charge && (
                      <p className="text-sm text-green-700 mt-2 font-medium">
                        ✅ Full Payment Settled: {newLuggage.currency} {parseFloat(newLuggage.total_charge || 0).toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>

                <Button type="submit" className="w-full btn-panashe" disabled={creating}>
                  {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Create Luggage Ticket
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by reference, sender, receiver..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="luggage-search"
          />
        </div>

        {/* Luggage List */}
        <div className="grid gap-4">
          {filteredLuggage.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-slate-500">
                <Package className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                <p>No luggage tickets found</p>
              </CardContent>
            </Card>
          ) : (
            filteredLuggage.map((item) => (
              <Card key={item.id} className={`hover:shadow-md transition-shadow ${item.is_fragile ? 'border-yellow-400 border-2' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <span className="font-mono font-bold text-lg">{item.luggage_ref}</span>
                        <Badge className={getStatusColor(item.status)}>
                          {item.status.replace('_', ' ').toUpperCase()}
                        </Badge>
                        {item.is_fragile && (
                          <Badge className="bg-yellow-100 text-yellow-800">⚠️ FRAGILE</Badge>
                        )}
                        {item.is_pay_forward && (
                          <Badge className="bg-orange-100 text-orange-800">💰 PAY FORWARD</Badge>
                        )}
                      </div>
                      
                      <p className="text-slate-700">{item.description}</p>
                      {item.item_type && (
                        <p className="text-sm text-slate-500 capitalize">Type: {item.item_type}</p>
                      )}
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
                        <div className="flex items-center gap-1">
                          <Package className="h-4 w-4 text-slate-400" />
                          Qty: {item.quantity || 1}
                        </div>
                        <div className="flex items-center gap-1">
                          <User className="h-4 w-4 text-slate-400" />
                          {item.sender_name}
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin className="h-4 w-4 text-slate-400" />
                          {item.origin} → {item.destination}
                        </div>
                        {item.receiver_name && (
                          <div className="flex items-center gap-1">
                            <Phone className="h-4 w-4 text-slate-400" />
                            To: {item.receiver_name}
                          </div>
                        )}
                      </div>
                      
                      {/* Pay Forward Info */}
                      {item.is_pay_forward && item.pay_forward_balance > 0 && (
                        <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-300 space-y-2">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div>
                              <p className="text-sm text-amber-950 font-bold">
                                💰 Pay-Forward Balance Due: <span className="text-red-700 font-extrabold">{item.currency === 'ZAR' ? 'R' : '$'}{item.pay_forward_balance.toFixed(2)}</span>
                              </p>
                              <p className="text-xs text-red-600 font-medium mt-0.5 flex items-center gap-1">
                                🚫 DELIVERY LOCKED: Must collect payment before releasing parcel
                              </p>
                            </div>
                            <Button
                              size="sm"
                              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs h-8 shadow-sm"
                              onClick={() => openCollectDialog(item)}
                            >
                              💰 Collect Payment
                            </Button>
                          </div>
                          <p className="text-[11px] text-slate-500">
                            Can be collected at either Zimbabwe or South Africa station counter. Once paid, the receipt updates automatically.
                          </p>
                        </div>
                      )}

                      {/* If balance was cleared across stations */}
                      {item.balance_cleared && (
                        <div className="mt-2 p-2.5 bg-green-50 rounded-lg border border-green-300 text-xs">
                          <p className="text-green-800 font-bold flex items-center gap-1">
                            ✓ Pay-Forward Balance Cleared in Full
                          </p>
                          <p className="text-green-700 mt-0.5">
                            Received at: <span className="font-semibold">{item.balance_cleared_station || 'Station Counter'}</span> by <span className="font-semibold">{item.balance_cleared_by_name || 'Staff'}</span>
                          </p>
                          <p className="text-slate-600 font-medium mt-0.5">
                            ✅ Cleared for release to receiver at destination station!
                          </p>
                        </div>
                      )}
                      
                      {/* Declared Value */}
                      {item.declared_value > 0 && (
                        <p className="text-xs text-slate-500 mt-2">
                          Declared Value: <span className="font-semibold text-slate-700">{item.declared_value_currency === 'USD' ? '$' : 'R'}{item.declared_value.toFixed(2)} ({item.declared_value_currency || 'ZAR'})</span>
                        </p>
                      )}
                    </div>
                    
                    <div className="flex flex-col items-center gap-2 ml-4">
                      {/* Photo Preview or QR Code */}
                      {item.photo_url ? (
                        <div className="w-20 h-20 bg-white border rounded overflow-hidden">
                          <img 
                            src={item.photo_url} 
                            alt="Luggage" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-20 h-20 bg-white border rounded p-1">
                            <QRCodeSVG value={getSystemDetailsUrl(item.luggage_ref, 'luggage')} size={96} level="H" includeMargin />
                        </div>
                      )}
                      <div className="flex gap-1 flex-wrap justify-center">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate(`/luggage-ticket/${item.luggage_ref}`)}
                        >
                          <Printer className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        {/* Photo Upload Button */}
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handlePhotoUpload(item.luggage_ref, e)}
                            disabled={uploadingPhoto}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            asChild
                            className={item.photo_url ? 'border-green-500 text-green-700' : ''}
                          >
                            <span>
                              {uploadingPhoto ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Camera className="h-4 w-4" />
                              )}
                            </span>
                          </Button>
                        </label>
                        {/* Release / Deliver Button */}
                        {item.status !== 'claimed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className={item.pay_forward_balance > 0 ? 'border-red-300 text-red-600 hover:bg-red-50' : 'border-green-600 text-green-700 hover:bg-green-50 font-semibold'}
                            onClick={() => handleDeliverLuggage(item)}
                            disabled={deliveringRef === item.luggage_ref}
                            title={item.pay_forward_balance > 0 ? "Cannot release: pay-forward balance unpaid!" : "Release parcel to receiver"}
                          >
                            {deliveringRef === item.luggage_ref ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>📦 Release</>
                            )}
                          </Button>
                        )}
                        {!['claimed', 'delivered', 'cancelled'].includes(item.status) && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-orange-400 text-orange-700 hover:bg-orange-50"
                            onClick={() => {
                              const nextTrips = getNextTransferTrips(item);
                              setTransferItem(item);
                              setTransferTargetTripId(nextTrips[0]?.id || '');
                              setTransferReason('bus_full');
                            }}
                            disabled={transferringRef === item.luggage_ref}
                            title="Move this luggage to the next trip when the current bus is full"
                          >
                            {transferringRef === item.luggage_ref ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>🚐 Transfer</>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        <Dialog open={Boolean(transferItem)} onOpenChange={(open) => !open && setTransferItem(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Transfer luggage to another trip</DialogTitle>
            </DialogHeader>
            {transferItem && (
              <div className="space-y-4">
                <div className="rounded-lg bg-orange-50 border border-orange-200 p-3 text-sm text-orange-900">
                  <p className="font-semibold">{transferItem.luggage_ref}</p>
                  <p>{transferItem.description}</p>
                  <p className="mt-1">Current trip: {transferItem.trip_id}</p>
                </div>

                <div>
                  <Label>Scheduled trip on the same route</Label>
                  {getNextTransferTrips(transferItem).length > 0 ? (
                    <Select value={transferTargetTripId} onValueChange={setTransferTargetTripId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose another trip" />
                    </SelectTrigger>
                    <SelectContent>
                      {getNextTransferTrips(transferItem).map((trip) => (
                        <SelectItem key={trip.id} value={trip.id}>
                          {trip.route_name} · {trip.departure_datetime && format(parseISO(trip.departure_datetime), 'dd MMM HH:mm')} · {trip.bus_registration || 'Bus'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                    </Select>
                  ) : (
                    <p className="mt-2 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                      No other scheduled trip is available for this route. Schedule another trip before transferring this luggage.
                    </p>
                  )}
                </div>

                <div>
                  <Label>Reason</Label>
                  <Select value={transferReason} onValueChange={setTransferReason}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bus_full">Bus full</SelectItem>
                      <SelectItem value="next_trip_only">Move to next trip</SelectItem>
                      <SelectItem value="consolidation">Consolidate cargo for same route</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setTransferItem(null)}>Cancel</Button>
                  <Button className="bg-orange-600 hover:bg-orange-700 text-white" onClick={handleTransferLuggage} disabled={!transferTargetTripId || transferringRef === transferItem.luggage_ref}>
                    {transferringRef === transferItem.luggage_ref ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Confirm transfer
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Collect Balance Dialog */}
        <Dialog open={collectDialogOpen} onOpenChange={setCollectDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2 text-amber-900">
                💰 Collect Pay-Forward Balance
              </DialogTitle>
            </DialogHeader>
            {collectItem && (
              <div className="space-y-4 mt-2">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs space-y-1">
                  <p className="font-bold text-amber-900">Luggage Ref: {collectItem.luggage_ref}</p>
                  <p className="text-slate-700">{collectItem.description} ({collectItem.sender_name} → {collectItem.receiver_name})</p>
                  <p className="text-amber-800 font-bold text-sm mt-1">
                    Outstanding Balance: {collectItem.currency === 'ZAR' ? 'R' : '$'}{collectItem.pay_forward_balance?.toFixed(2)}
                  </p>
                  <p className="text-slate-500 text-[11px] mt-0.5">
                    💡 Once recorded, the receipt updates immediately so destination staff can release the luggage!
                  </p>
                </div>

                <div>
                  <Label>Amount Collected *</Label>
                  <div className="flex gap-2 mt-1">
                    <Select value={collectCurrency} onValueChange={setCollectCurrency}>
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="ZAR">ZAR (R)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      step="0.01"
                      value={collectAmount}
                      onChange={(e) => setCollectAmount(e.target.value)}
                      placeholder="Amount"
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <Label>Notes &amp; Station</Label>
                  <Input
                    value={collectNotes}
                    onChange={(e) => setCollectNotes(e.target.value)}
                    placeholder="e.g., Paid cash at Johannesburg station counter"
                  />
                </div>

                <Button
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold"
                  onClick={handleCollectBalanceSubmit}
                  disabled={collectSubmitting}
                >
                  {collectSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Confirm &amp; Update Pay-Forward Receipt
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default LuggagePage;
